import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Header,
  Message,
  Segment,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import _ from "lodash";
import {
  isPartyCoAgentUser,
  isPartyTcUser,
  isPartyAssistantUser,
  buildAgentCoAgentContextLines,
} from "../../../app/common/util/util";
import {
  sendDocSharingEmail,
  addHistoryToDb,
  updateDocAddSharingInDb,
  updateDocInDb,
  updateTransAddSharingInDb,
  updatePartyInDb,
} from "../../../app/firestore/firestoreService";

export default function AgentShare({ doc, party, transaction, allParties }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [selectedClients, setSelectedClients] = useState([]);
  const [processing, setProcessing] = useState(false);
  const { currentUser } = useSelector((state) => state.auth);
  const { currentUserProfile } = useSelector((state) => state.profile);

  // Co-agents, TCs, and assistants share the same party portal link as the
  // agent — figure out who's actually logged in so the sharing email gets
  // signed by them, not the agent on record.
  const loggedInEmail = currentUser?.email || currentUserProfile?.email;
  const isCoAgentUser = isPartyCoAgentUser(party, loggedInEmail);
  const isTcUser = isPartyTcUser(party, loggedInEmail);
  const isAssistantUser = isPartyAssistantUser(party, loggedInEmail);
  const actualSender = isCoAgentUser
    ? { ...party.coAgent, isTester: party.isTester }
    : isTcUser
    ? { ...party.transactionCoordinator, isTester: party.isTester }
    : isAssistantUser
    ? { ...party.assistant, isTester: party.isTester }
    : party;
  const agentContextLines =
    isCoAgentUser || isTcUser || isAssistantUser
      ? buildAgentCoAgentContextLines(party)
      : undefined;
  // Role to log in the client's "Documents Shared" history when it's not the
  // agent themselves sharing.
  const actualSenderRole = isCoAgentUser
    ? "CoAgent"
    : isTcUser
    ? "TC"
    : isAssistantUser
    ? "Assistant"
    : null;

  // Get this agent's own clients from the transaction parties.
  // The agent viewing the client portal is on the opposite side of the
  // TransActioner subscriber, so their clients are the "other side" parties.
  const ownClientParties = useMemo(() => {
    if (!allParties || !transaction.agentRepresents) {
      return [];
    }

    let ownClientRoles = [];
    if (transaction.agentRepresents === "Buyer") {
      ownClientRoles = ["Seller", "Seller 2", "Seller 3"];
    } else if (transaction.agentRepresents === "Seller") {
      ownClientRoles = ["Buyer", "Buyer 2", "Buyer 3"];
    }

    return allParties.filter((p) => ownClientRoles.includes(p.role));
  }, [allParties, transaction.agentRepresents]);

  // Build initial client list with auto-populated names from transaction parties
  useEffect(() => {
    const initialClients = ownClientParties.map((p) => {
      const hasName = !!(p.firstName && p.lastName);
      const fullName = hasName ? `${p.firstName} ${p.lastName}` : "";
      return {
        odPartyId: p.id,
        role: p.role,
        fullName: fullName,
        email: p.email || "",
        isFromTransaction: hasName,
        selected: true,
      };
    });
    setSelectedClients(initialClients);
  }, [ownClientParties]);

  function handleToggleClient(index) {
    const updatedClients = [...selectedClients];
    updatedClients[index].selected = !updatedClients[index].selected;
    setSelectedClients(updatedClients);
  }

  function handleClientChange(index, field, value) {
    const updatedClients = [...selectedClients];
    if (field === "email") {
      value = value.toLowerCase().trim();
    }
    updatedClients[index][field] = value;
    setSelectedClients(updatedClients);
  }

  async function handleShare() {
    setProcessing(true);
    try {
      const clientsToShare = selectedClients.filter((client) => client.selected);

      if (clientsToShare.length === 0) {
        toast.error("Please select at least one client to share with.");
        setProcessing(false);
        return;
      }

      const invalidClients = clientsToShare.filter(
        (client) => !client.fullName || !client.email
      );

      if (invalidClients?.length > 0) {
        toast.error("Please fill in all required fields for selected clients.");
        setProcessing(false);
        return;
      }

      const invalidClientEmails = clientsToShare.filter(
        (client) =>
          !client.email ||
          !/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
            client.email
          )
      );

      if (invalidClientEmails?.length > 0) {
        toast.error(
          "Please correct the email address: this should only be a single valid email address for each client."
        );
        setProcessing(false);
        return;
      }

      // Build full client party objects, persist any updated emails, then share
      const clientsForEmail = [];
      for (const client of clientsToShare) {
        const originalParty = ownClientParties.find(
          (p) => p.id === client.odPartyId
        );
        const nameParts = client.fullName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        const clientForSharing = {
          ...originalParty,
          role: client.role,
          email: client.email,
          firstName,
          lastName,
        };

        if (originalParty && originalParty.email !== client.email) {
          await updatePartyInDb(originalParty, { email: client.email });
        }

        await updateDocAddSharingInDb(doc.id, clientForSharing, transaction);

        // Add the client to the transaction's sharingWith so the transaction
        // appears in their Client Portal (the portal queries transactions by
        // sharingWith). Without this the document is shared but the client
        // can't see the transaction until it's sent for signing.
        await updateTransAddSharingInDb(
          transaction.id,
          clientForSharing,
          transaction
        );

        // Log the actual sender (agent/CoAgent/TC/assistant), not always the
        // agent on record, so "Documents Shared" shows who really did it.
        addHistoryToDb(
          transaction.id,
          actualSender,
          "shared",
          doc.name,
          clientForSharing,
          actualSenderRole,
          party.id
        );

        // Store per-client sharing details (name, email, date) so the button
        // can display them on hover after the document has been shared.
        const roleCamel = _.camelCase(client.role);
        updateDocInDb(
          doc.id,
          {
            [`sharedWithDetails.${roleCamel}`]: {
              name: `${firstName} ${lastName}`.trim(),
              email: client.email,
              sharedAt: new Date(),
            },
          },
          true // noUpdatedAt – don't bump the document's updatedAt
        );

        clientsForEmail.push(clientForSharing);
      }

      // restrictedToPartyId keeps this out of the main transaction's Email History —
      // it's this agent's own team sharing with their own clients, not visible to the other side.
      // actualSender signs the email as whoever actually sent it (agent/CoAgent/TC/assistant);
      // agentContextLines adds the agent/CoAgent-on-record info when it's not the agent.
      // sendDocSharingEmail also logs the exact content sent to Email History.
      await sendDocSharingEmail(
        clientsForEmail,
        "sharing",
        actualSender,
        transaction,
        doc,
        party.id,
        agentContextLines
      );

      setProcessing(false);
      toast.success("Document successfully shared with selected clients");
      dispatch(closeModal({ modalType: "AgentShare" }));
    } catch (error) {
      toast.error(error.message);
      setProcessing(false);
    }
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "AgentShare" }));
  }

  return (
    <ModalWrapper size="small">
      <Segment>
        <Grid>
          <Grid.Column width={16} className="zero bottom padding">
            <Header as="h2" color="blue" className="zero bottom margin">
              Share Document
            </Header>
            <Divider className="zero bottom margin" />
            <p>
              Select your clients to share this document with.
              <br />
              Add or update the email address for your clients.
              <br />
              <i>
                The document will appear in your clients' Client Portal so they
                can view it.
              </i>
            </p>
          </Grid.Column>

          <Grid.Column width={16}>
            {selectedClients.length === 0 ? (
              <Message warning>
                <Message.Header>No Clients Found</Message.Header>
                <p>
                  There are no clients on your side of this transaction to share
                  with. Add your clients in the transaction's Parties section
                  first.
                </p>
              </Message>
            ) : (
              <Formik
                initialValues={{}}
                validationSchema={Yup.object({})}
                onSubmit={() => {}}
              >
                {() => (
                  <FormikForm>
                    {!isMobile && (
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={1}></Grid.Column>
                          <Grid.Column width={3}>Role</Grid.Column>
                          <Grid.Column width={5}>Name</Grid.Column>
                          <Grid.Column width={7}>Email</Grid.Column>
                        </Grid.Row>
                      </Grid>
                    )}
                    {selectedClients.map((client, index) => (
                      <Segment key={index} style={{ marginBottom: "10px" }}>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={isMobile ? 2 : 1}>
                              <MyCheckbox
                                name={`selected_${index}`}
                                checked={client.selected}
                                onChange={() => handleToggleClient(index)}
                              />
                            </Grid.Column>
                            <Grid.Column width={isMobile ? 14 : 3}>
                              <Header as="h4" style={{ margin: 0 }}>
                                {client.role}
                              </Header>
                            </Grid.Column>
                            <Grid.Column width={isMobile ? 16 : 5}>
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={client.fullName}
                                onChange={(e) =>
                                  handleClientChange(
                                    index,
                                    "fullName",
                                    e.target.value
                                  )
                                }
                                disabled={client.isFromTransaction}
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  border: "1px solid rgba(34,36,38,.15)",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  backgroundColor: client.isFromTransaction
                                    ? "#f9f9f9"
                                    : "white",
                                  cursor: client.isFromTransaction
                                    ? "not-allowed"
                                    : "text",
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={isMobile ? 16 : 7}>
                              <input
                                type="email"
                                placeholder="Email"
                                value={client.email}
                                onChange={(e) =>
                                  handleClientChange(
                                    index,
                                    "email",
                                    e.target.value
                                  )
                                }
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  border: "1px solid rgba(34,36,38,.15)",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Segment>
                    ))}
                  </FormikForm>
                )}
              </Formik>
            )}
          </Grid.Column>

          <Grid.Column width={16} className="zero top padding">
            <Divider className="zero top margin" />
            {processing ? (
              <Button
                primary
                loading
                floated={isMobile ? null : "right"}
                className={isMobile ? "fluid medium bottom margin" : null}
              >
                Sharing...
              </Button>
            ) : (
              <Button
                floated={isMobile ? null : "right"}
                primary
                onClick={handleShare}
                className={isMobile ? "fluid medium bottom margin" : null}
              >
                Share
              </Button>
            )}

            <Button
              floated={isMobile ? null : "right"}
              onClick={handleCancel}
              className={isMobile ? "fluid medium bottom margin" : null}
            >
              Cancel
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>
    </ModalWrapper>
  );
}
